import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  ListItemButton,
  Stack,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { FormProvider, useForm } from 'react-hook-form';
import { FaRegBuilding } from 'react-icons/fa';
import { LuCopy } from 'react-icons/lu';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ICandidate,
  IJob,
  amplitude,
  getJobById,
  IMappingFormSettingsResponse,
  getCandidateResumeByFileUrl,
  fetchCandidateResponse,
  getCandidateSummary,
  usersAdvanceFilterQuery,
  submitCandidate,
  updateCandidateSubmission,
} from 'wisestep-api';
import {
  ControlledTextArea,
  IConversationState,
  timeSince,
  WsButton,
  WsColors,
  WsDialog,
  WsDrawer,
  WsElementWithPopOver,
  WsIconAssignCandidate,
  WsIconMoreVer,
  WsIconText,
  WsListingLoader,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
  WsIconPencil,
  WsIconMonyBag,
  WsIconCircleCheck,
  WsIconSummary,
  WsAutoComplete,
} from 'wisestep-base';

import UpdateSalaryModal from '../../../../components/UpdateSalaryModal/index';
import UpdateStatusModal from '../../../../components/UpdateStatusModal/index';
import { useWorkspaceContext } from '../../../../context';
import { ClintPageBreadCrumb } from '../../../../features/clientDetails/helper/clientDetailsHelper';
import MappingForm from '../../../../features/conversationWidget/components/MappingForm';
import conversationWidgetStore from '../../../../features/conversationWidget/store/conversationWidgetStore';
import { useListingSettings } from '../../../../features/jobs/listing/hooks';
import StageStatusContextProvider from '../../../../features/submissions/context/StageStatusContext';
import useMappingFormSettings from '../../../../hooks/useMappingFormSettings';
import ErrorLogger from '../../../../providers/ErrorLogger';
import getEncodedQueryParams from '../../../../utils/getEncodedQueryParams';
import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { getAccountManagerAnalyticsUrl } from '../../../candidates/utils/CandidateHelper';
import { MapAsSubmission } from '../../../jobs/listing/components/mapAsSubmission';
import { copyStringToClipboard } from '../childComponets/CandidateDetailsCard/Helper';

export interface MappingBannerProps {
  interaction: ICandidate;
  updateInteraction?: (interaction: ICandidate) => void;
  state?: IConversationState | null;
  candidateIndex?: number;
  isSubmissions?: boolean;
  isMapping?: boolean;
}

interface IRecruiterSuggestion {
  userId: string;
  userName: string;
}

const MappingBanner = ({
  interaction,
  candidateIndex,
  isSubmissions,
  updateInteraction,
  state,
}: MappingBannerProps) => {
  const navigate = useNavigate();
  const { jobFieldSettings } = useListingSettings();
  const admin = useWorkspaceContext();
  const [openStatusModal, setOpenStatusModal] = useState<boolean>(false);
  const [openSalaryModal, setOpenSalaryModal] = useState<boolean>(false);
  const openUpdateStatusModal = () => setOpenStatusModal(true);
  const openUpdateSalaryModal = () => setOpenSalaryModal(true);
  const closeUpdateSalaryModal = () => setOpenSalaryModal(false);
  const closeUpdateStatusModal = () => {
    setOpenStatusModal(false);
  };
  const [showSubmissionForm, setShowSubmissionForm] = useState<boolean>(false);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [jobData, setJobData] = useState<IJob>();
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const closeModal = () => close();
  const mappingFormSettingsResponse = useMappingFormSettings();
  const [mappingFormSettings, setMappingFormSetings] = useState<
    IMappingFormSettingsResponse | undefined
  >();
  const defaultValues = {
    candidateSummary: '',
    assignedRecruiters: [''],
  };
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState<boolean>(false);
  const [recruiterSuggestions, setRecruiterSuggestions] = useState<IRecruiterSuggestion[]>([]);

  const methods = useForm({ defaultValues });
  const { watch, setValue, handleSubmit, getValues } = methods;
  const [isCandidateSummaryGenerated, setIsCandidateSummaryGenerated] = useState<boolean>(false);
  const selectedJob = conversationWidgetStore((state) => state.selectedJob);
  const location = useLocation();
  const { from: previousPath = '', submittedJobId: submittedJobId = '' } =
    (location.state as { from?: string; submittedJobId?: string }) || {};

  useEffect(() => {
    if (previousPath.includes('all-submissions')) {
      setShowSubmissionForm(true);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getCandidateSummary(interaction.id ?? '');
        const candidateJobSummary = res.data?.candidateJobSummary ?? '';
        setValue('candidateSummary', candidateJobSummary);
        setIsCandidateSummaryGenerated(candidateJobSummary.length > 0);
      } catch (e) {
        console.log(e);
      }
    })();
  }, [interaction.id, setValue]);

  useEffect(() => {
    const fetchJobById = async () => {
      try {
        const jobId = (interaction.jobId as string) || submittedJobId || '';
        const response = await getJobById(jobId);
        setJobData(response.data);
      } catch (error) {
        console.error('Error fetching job:', error);
      }
    };
    fetchJobById();
    if (mappingFormSettingsResponse.fields)
      setMappingFormSetings(mappingFormSettingsResponse.fields);
  }, [interaction.jobId, mappingFormSettingsResponse.fields, submittedJobId]);

  const handleEditClick = () => {
    setShowSubmissionForm(true);
  };
  const handleChatGPT = async (state) => {
    try {
      amplitude('Save_Candidate_Summary', {
        clickedOn: 'Save Button',
        pageName: 'candidate-layout',
        pageUrl: window.location.href,
      });
      const res = await updateCandidateSubmission([
        {
          candidateJobSummary: state?.candidateSummary ?? '',
          id: interaction.id,
        } as ICandidate,
      ]);
      setValue('candidateSummary', res?.data?.[0]?.candidateJobSummary ?? '');
      setNotification(success('Candidate Summary successfully saved'));
      setIsCandidateSummaryGenerated(true);
    } catch (e) {
      setNotification(failed('!Something Went Wrong.Try again'));
      console.log(e);
    } finally {
      setOpenDialog(false);
    }
  };
  const copy = () => {
    amplitude('Copy_Candidate_Summary', {
      clickedOn: 'Copy Button',
      pageName: 'candidate-layout',
      pageUrl: window.location.href,
    });
    copyStringToClipboard(getValues('candidateSummary'));
    setNotification(success('Copied Successfully !!'));
  };

  const getresumeString = async () => {
    try {
      const res = await getCandidateResumeByFileUrl(interaction.resumeLocation ?? '');
      const response = await fetchCandidateResponse({
        resumeContent: res.data?.content,
        jobDescription: jobData?.description ?? '',
      });

      setValue('candidateSummary', response.data);
    } catch (e) {
      setNotification(failed('!Please Upload Proper CV & Try again'));
      setOpenDialog(false);
      console.log(e);
    }
  };
  const handleCloseChapgptDialog = () => {
    setOpenDialog(false);
    !isCandidateSummaryGenerated && setValue('candidateSummary', '');
  };

  const onGenerateSummary = () => {
    setOpenDialog(true);
    !isCandidateSummaryGenerated && getresumeString();
    amplitude('Generate_Candidate_Summary', {
      clickedOn: 'Generate Summary',
      pageName: 'candidate-layout',
      pageUrl: window.location.href,
    });
  };

  const onUpdateSubmittedby = () => {
    setOpenUpdateDialog(true);
  };

  const handleEdit = () => {
    handleEditClick();
    amplitude('CandidateView_Submission_EditSubmission', {
      clickedOn: 'Edit Submission',
      pageName: 'candidate-layout',
      pageUrl: window.location.href,
    });
  };

  const handleUpdateSalary = () => {
    openUpdateSalaryModal();
    amplitude('CandidateView_Submission_UpdateStatus', {
      clickedOn: 'Submission UpdateStatus',
      pageName: 'candidate-layout',
      pageUrl: window.location.href,
    });
  };

  const updateStatus = () => {
    openUpdateStatusModal();
    amplitude('CandidateView_Submission_UpdateStatus', {
      clickedOn: 'Submission UpdateStatus',
      pageName: 'candidate-layout',
      pageUrl: window.location.href,
    });
  };

  const handleInput = async (input: string) => {
    const response = await usersAdvanceFilterQuery(input);
    setRecruiterSuggestions(
      response.data.map((recruiter) => ({
        userId: recruiter?.userId,
        userName: recruiter?.userName,
      }))
    );
    return;
  };

  const assignRecruiter = async () => {
    setOpenUpdateDialog(false);
    const selected = methods.getValues('assignedRecruiters');
    const selectedRecruiter = recruiterSuggestions.find(
      (recruiter) => recruiter.userId === selected[0]
    );
    const updatedInteraction = {
      ...interaction,
      recruiterId: selectedRecruiter?.userId ?? 'NA',
      recruiterName: selectedRecruiter?.userName ?? 'NA',
    };
    try {
      await submitCandidate(updatedInteraction);
      setNotification(success(`Candidate updated successfully`));
      window.location.reload();
    } catch {
      setNotification(warning('Something went wrong, please try again.'));
    }
  };

  return (
    <>
      <Box
        px={4}
        sx={{
          minHeight: 100,
          alignItems: 'center',
          backgroundColor: WsColors.TertiaryShades50,
          borderRadius: 2,
        }}
      >
        <Grid container pt={1} display="flex" justifyContent="space-between">
          <Grid item py={1}>
            <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.BLACK} bold>
              {interaction.mappedJobTitle}
            </WsTypography>
          </Grid>
          {isSubmissions && (
            <Grid item display="flex" alignItems="center" columnGap={2} py={1}>
              <WsElementWithPopOver element={<WsIconMoreVer />}>
                <Stack p={1}>
                  {
                    <>
                      <ListItemButton>
                        <WsIconText
                          text="Edit"
                          icon={<WsIconPencil />}
                          onClick={handleEdit}
                          textSize={WsTypoVariants.CAPTION}
                        />
                      </ListItemButton>
                      <ListItemButton>
                        <WsIconText
                          text="Update Salary"
                          onClick={() => handleUpdateSalary()}
                          icon={<WsIconMonyBag width={24} height={24} />}
                          textSize={WsTypoVariants.CAPTION}
                        />
                      </ListItemButton>
                      <ListItemButton>
                        <WsIconText
                          text="Update Status"
                          onClick={updateStatus}
                          icon={<WsIconCircleCheck />}
                          textSize={WsTypoVariants.CAPTION}
                        />
                      </ListItemButton>
                      <ListItemButton>
                        <WsIconText
                          text={!isCandidateSummaryGenerated ? 'Generate Summary' : 'View Summary'}
                          icon={<WsIconSummary />}
                          onClick={onGenerateSummary}
                          textSize={WsTypoVariants.CAPTION}
                        />
                      </ListItemButton>
                      {admin?.owns && (
                        <ListItemButton>
                          <WsIconText
                            text={'Update Recruiter'}
                            icon={<WsIconSummary />}
                            onClick={onUpdateSubmittedby}
                            textSize={WsTypoVariants.CAPTION}
                          />
                        </ListItemButton>
                      )}
                    </>
                  }
                </Stack>
              </WsElementWithPopOver>
            </Grid>
          )}
          {/* {isMapping && (
              <Box display="flex" alignItems="center">
                <WsButton
                  className="update-status"
                  variant="outlined"
                  id="update_status"
                  size="medium"
                  onClick={() => {
                    setOpenDrawer(true);
                  }}
                >
                  Update
                </WsButton>
              </Box>
            )} */}

          <ErrorBoundary FallbackComponent={({ error }) => <ErrorLogger error={error} />}>
            {openStatusModal && (
              <StageStatusContextProvider>
                <UpdateStatusModal
                  candidate={interaction}
                  candidateIndex={candidateIndex}
                  openStatusModal={openStatusModal}
                  onClose={closeUpdateStatusModal}
                  updateStageStatus={() => window.location.reload()}
                  jobFieldSettings={jobFieldSettings}
                />
              </StageStatusContextProvider>
            )}
            {openSalaryModal && (
              <UpdateSalaryModal
                onClose={closeUpdateSalaryModal}
                job={jobData}
                interaction={interaction}
                updateInteraction={updateInteraction}
              />
            )}
            {openDrawer && (
              <Grid item xs={12}>
                <WsDrawer
                  open={true}
                  size="medium"
                  hideHeader={true}
                  onClose={() => setOpenDrawer(false)}
                >
                  <MappingForm
                    state={state}
                    recruiterAction={''}
                    selectedJob={selectedJob}
                    closeDrawer={() => window.location.reload()}
                    mappingFormSettings={mappingFormSettings}
                    conversation={interaction}
                    onClose={() => setOpenDrawer(false)}
                  />
                </WsDrawer>
              </Grid>
            )}
          </ErrorBoundary>
        </Grid>
        <Grid container direction="row" pl={1} pt={1} spacing={2}>
          {interaction.jobCode && (
            <Grid item>
              <WsIconText
                icon={
                  <BusinessCenterOutlinedIcon
                    style={{ color: WsColors.TertiaryShades600, fontSize: 20 }}
                  />
                }
                text={interaction.jobCode}
                textSize={WsTypoVariants.BODY1}
                textColor={WsTypoColors.TERTIARY900}
                popOverText="Job Code"
                textCursor="pointer"
              />
            </Grid>
          )}
          <Grid item>
            <WsIconText
              icon={<WsIconAssignCandidate color={WsColors.TertiaryShades600} height={19} />}
              text={
                interaction.jobAccountManagerName ??
                interaction?.jobAccountManagerEmail ??
                'Not available'
              }
              textSize={WsTypoVariants.BODY1}
              textColor={
                interaction.jobAccountManagerName || interaction.jobAccountManagerEmail
                  ? WsTypoColors.PRIMARY600
                  : WsTypoColors.TERTIARY900
              }
              popOverText="Account Manager"
              textCursor="pointer"
              onClick={async () => {
                const url =
                  interaction.jobAccountManagerEmail &&
                  (await getAccountManagerAnalyticsUrl(interaction?.jobAccountManagerEmail ?? ''));
                url && navigate(url);
              }}
            />
          </Grid>
          <Grid item>
            <WsIconText
              icon={
                <AccessTimeOutlinedIcon
                  style={{ color: WsColors.TertiaryShades600, fontSize: 20 }}
                />
              }
              text={timeSince(interaction.modifiedOn ?? '')}
              textSize={WsTypoVariants.BODY1}
              textColor={WsTypoColors.TERTIARY900}
              popOverText="Last Updated"
              textCursor="pointer"
            />
          </Grid>
          {interaction?.companyName && !/.*xxxxxx.*/.test(interaction.companyName) ? (
            <Grid item>
              <Link
                href={`/client-details?tabIndex=0&isChild=true&name=${
                  jobData?.companyName
                }&jobCode=${jobData?.jobCode}&breadCrumbList=${getEncodedQueryParams(
                  ClintPageBreadCrumb['CLIENTS']
                )}`}
                underline="none"
              >
                <WsIconText
                  icon={
                    <FaRegBuilding style={{ color: WsColors.TertiaryShades600, fontSize: 20 }} />
                  }
                  text={interaction.companyName ?? 'Not available'}
                  textSize={WsTypoVariants.BODY1}
                  textColor={WsTypoColors.PRIMARY}
                  popOverText="Client"
                  textCursor="pointer"
                />
              </Link>
            </Grid>
          ) : (
            <Grid item>
              <WsIconText
                icon={<FaRegBuilding style={{ color: WsColors.TertiaryShades600, fontSize: 20 }} />}
                text={interaction.companyName ?? 'Not available'}
                textSize={WsTypoVariants.BODY1}
                textColor={WsTypoColors.PRIMARY}
                popOverText="Client"
              />
            </Grid>
          )}
        </Grid>
      </Box>

      {showSubmissionForm && jobData && (
        <WsDrawer
          style={{ zIndex: '1300' }}
          size="medium"
          hideHeader={true}
          open={showSubmissionForm}
          onClose={() => closeModal}
          hideOverflow={true}
        >
          <MapAsSubmission
            selectedJob={jobData}
            onClose={() => setShowSubmissionForm(false)}
            selectedCandidate={interaction}
            hideJobSelection={true}
          />
        </WsDrawer>
      )}
      <FormProvider {...methods}>
        <WsDialog openDialog={openDialog} onClose={handleCloseChapgptDialog} size={'small'}>
          <form onSubmit={handleSubmit(handleChatGPT)}>
            <DialogTitle>
              <Grid container direction="row" justifyContent="space-between">
                <Grid item>
                  <WsTypography variant={WsTypoVariants.TITLE} bold={true}>
                    Generated Candidate Summary
                  </WsTypography>
                </Grid>
                <Grid item>
                  <IconButton onClick={handleCloseChapgptDialog} sx={{ padding: 0 }}>
                    <CloseIcon sx={{ fill: WsColors.GreyBlack, cursor: 'pointer' }} />
                  </IconButton>
                </Grid>
              </Grid>
            </DialogTitle>
            {watch('candidateSummary').length == 0 ? (
              <Grid>
                <WsListingLoader>
                  <WsTypography variant={WsTypoVariants.TITLE} bold>
                    Please Wait... Generating Summary
                  </WsTypography>
                </WsListingLoader>
              </Grid>
            ) : (
              <DialogContent dividers sx={{ px: 3 }}>
                <Grid container direction="column" rowSpacing={1}>
                  <Grid item>
                    <WsTypography variant={WsTypoVariants.TITLE}>Candiadte Summary</WsTypography>
                  </Grid>
                  <Grid item>
                    <ControlledTextArea
                      name="candidateSummary"
                      minRows={10}
                      disabled={isCandidateSummaryGenerated}
                    />
                  </Grid>
                  <Grid item>
                    <WsButton
                      startIcon={<LuCopy color={WsColors.TheBlues500} />}
                      onClick={copy}
                      variant="outlined"
                      size="medium"
                    >
                      Copy
                    </WsButton>
                  </Grid>
                </Grid>
              </DialogContent>
            )}
            {!isCandidateSummaryGenerated && (
              <DialogActions sx={{ p: 1 }}>
                <WsButton type="submit" disabled={watch('candidateSummary').length == 0}>
                  Save
                </WsButton>
              </DialogActions>
            )}
          </form>
        </WsDialog>
        <WsDialog size="medium" openDialog={openUpdateDialog}>
          <DialogContent sx={{ p: 2 }}>
            {<WsTypography variant={WsTypoVariants.TITLE}>Update Submitted by</WsTypography>}
            <Grid container spacing={2}>
              <Grid item xs={12} mt={2}>
                <Stack direction="column">
                  <WsTypography variant={WsTypoVariants.SUB_TITLE}>Email Address</WsTypography>
                </Stack>
                <WsAutoComplete
                  name="assignedRecruiters"
                  fullWidth
                  size="small"
                  multiple={false}
                  initialValue={['NA']}
                  optionsInStringArray={recruiterSuggestions.map((recruiter) => recruiter.userId)}
                  onValueEnter={(input) => handleInput(input)}
                  onTagsChange={(tags) => methods.setValue('assignedRecruiters', tags)}
                />
              </Grid>
              <Grid item xs={12} spacing={2}>
                <Stack direction="row" justifyContent="flex-end" spacing={2}>
                  <WsButton
                    variant="outlined"
                    size="large"
                    onClick={() => setOpenUpdateDialog(false)}
                  >
                    Cancel
                  </WsButton>
                  {
                    <WsButton type="submit" size="large" onClick={assignRecruiter}>
                      Assign
                    </WsButton>
                  }
                </Stack>
              </Grid>
            </Grid>
          </DialogContent>
        </WsDialog>
      </FormProvider>
    </>
  );
};

export default MappingBanner;
